import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { create as createModal, useModal } from "@ebay/nice-modal-react";
import { cx } from "@emotion/css";
import { App, Col, Form, Input, Modal, Row, Select, Space, Upload, message } from "antd";
import { useForm } from "antd/es/form/Form";
import Compressor from "compressorjs";
import map from 'lodash/map';
import pick from 'lodash/pick';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPicker } from "~/components/map-picker/map";
import { MembersInput } from "~/components/members-selection";
import { theme } from "~/config";
import { SvgIcon } from "~/icons";
import { ProjectManagementService } from "~/services";
import { Button, FormItem } from "~/styles";
import { defaultModalProps, formRules, globalNotificationConfig, useValidateForm } from "~/utils";
import style from "./style";
export const ProjectEditModal = createModal(({ project }) => {
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showMembers, setShowMembers] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const { notification } = App.useApp();
    const modal = useModal();
    const navigate = useNavigate();
    const [form] = useForm();
    const [isFormValid] = useValidateForm(form);
    const deleteModal = useModal("confirmation-modal");
    const saveModal = useModal("confirmation-modal");
    const projectCreatedModal = useModal("project-created-modal");
    const editMode = !!project;
    const initialValues = editMode ? // TODO: Add FormValues type
        {
            ...pick(project, ["aia_process", "description", "members", "name", "status"]),
            image: project.image ? [{ uid: '-1', name: 'image.png', status: 'done', 'url': project.image || "" }] : [],
            coordinates: `${project["latitude"]},${project["longitude"]}`
        }
        :
            { image: [], members: [] };
    useEffect(() => { ProjectManagementService.getUsers().then(m => setMembers(m)); }, []);
    const onDeleteClick = () => deleteModal
        .show({
        sentence: "Tem a certeza que quer apagar este projeto?",
        extraSentence: "Esta ação não pode ser revertida"
    })
        .then(() => ProjectManagementService.deleteProject(project.id))
        .then(() => notification.success({ message: 'Projeto apagado', ...globalNotificationConfig }))
        .then(defaultProps.onOk)
        .then(() => navigate('/projects/'))
        .catch(() => notification.error({ message: 'Erro a apagar o projeto', ...globalNotificationConfig }));
    const onSaveClick = () => {
        if (form.getFieldValue("status") == 1) {
            saveModal
                .show({
                sentence: "Ao alterar o estado do projeto para “Completo”, todas as visitas serão concluídas automaticamente, pretende continuar?",
                cancelLabel: "Cancelar",
                confirmationLabel: "Continuar",
                onCancel: () => { form.setFieldValue("status", initialValues["status"]); }
            })
                .then(() => onFinish(form.getFieldsValue()));
        }
        else {
            onFinish(form.getFieldsValue());
        }
    };
    const beforeImageUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            message.error('A imagem deve ser no formato JPG/PNG');
            return Upload.LIST_IGNORE;
        }
        const isLt4M = file.size / 1024 / 1024 < 4;
        if (!isLt4M) {
            message.error('A imagem deve ter no máximo 4MB');
            return Upload.LIST_IGNORE;
        }
        return false;
    };
    const confirmationModal = useModal("confirmation-modal");
    const onCancelClick = () => {
        confirmationModal.show({
            sentence: "Tem a certeza que quer descartar as alterações?",
            cancelLabel: 'Não', confirmationLabel: 'Sim'
        }).then(() => modal.hide());
    };
    const defaultProps = defaultModalProps(modal);
    const modalProps = {
        ...defaultProps,
        open: modal.visible,
        title: editMode ? "Editar projeto" : "Criar projecto",
        closeIcon: _jsx(SvgIcon, { name: "ic_close", size: 28, color: theme["neutral-100"] }),
        onCancel: onCancelClick,
        footer: (_jsxs(_Fragment, { children: [editMode && (_jsx(Button, { type: "link", icon: _jsx(SvgIcon, { name: "ic_trash" }), onClick: onDeleteClick, children: "Apagar" })), _jsxs(Space, { size: 24, children: [_jsx(Button, { onClick: onCancelClick, disabled: isLoading, children: "Cancelar" }), _jsx(Button, { type: "primary", onClick: onSaveClick, disabled: !isFormValid || isLoading, loading: isLoading, form: "project-form", children: editMode ? "Guardar alterações" : "Criar" })] })] }))
    };
    const handleUpload = (info) => {
        info.fileList.forEach((file) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const compressorInstance = new Compressor(file.originFileObj, {
                quality: 0.8,
                maxWidth: 500,
                success: (result) => {
                    file.originFileObj = result;
                    const reader = new FileReader();
                    reader.onload = () => file.base64 = reader.result.split(',').pop();
                    reader.readAsDataURL(file.originFileObj);
                },
                error: () => {
                    message.error('Erro a processar a imagem.');
                }
            });
        });
    };
    const onFinish = (formValues) => {
        setIsLoading(true);
        const data = {
            name: formValues['name'],
            description: formValues['description'],
            aia_process: formValues['aia_process'],
            status: formValues['status'],
            members: map(formValues.members, "sub"),
            latitude: formValues.coordinates.split(",")[0],
            longitude: formValues.coordinates.split(",")[1],
        };
        if (formValues.image.length)
            data["image"] = formValues["image"][0]["base64"];
        if (editMode) {
            let touchedFields = Object.keys(formValues).filter(form.isFieldTouched);
            if (touchedFields.includes("coordinates")) {
                touchedFields = touchedFields.filter(t => t !== 'coordinates');
                touchedFields.push("latitude", "longitude");
            }
            const body = pick(data, touchedFields);
            if (touchedFields.includes("image") && body["image"] == undefined) {
                body["image"] = null;
            }
            ProjectManagementService.updateProject(body, project.id)
                .then(() => notification.success({ message: "As suas alterações foram salvas", ...globalNotificationConfig }))
                .then(defaultProps.onOk)
                .catch(() => notification.error({ message: "Erro a atualizar o projeto.", ...globalNotificationConfig }))
                .finally(() => setIsLoading(false));
        }
        else {
            ProjectManagementService.createProject(data)
                .then(project => projectCreatedModal.show({ projectId: project.id }))
                .then(defaultProps.onOk)
                .catch(() => notification.error({ message: "Erro a criar o projeto.", ...globalNotificationConfig }))
                .finally(() => setIsLoading(false));
        }
    };
    return (_jsxs(Modal, { width: 944, className: cx(style(showMembers || showMap || projectCreatedModal.visible, editMode)), ...modalProps, children: [_jsx("div", { className: "type-body-4 mb-7", style: { color: theme["neutral-50"] }, children: "* Campo de preenchimento obrigat\u00F3rio" }), _jsx(Form, { id: "project-form", form: form, layout: "vertical", initialValues: initialValues, preserve: false, children: _jsxs(Row, { gutter: 24, children: [_jsx(Col, { span: editMode ? 24 : 18, children: _jsx(FormItem, { label: "Nome", name: "name", rules: [formRules.required], children: _jsx(Input, { placeholder: "Adicionar nome do projeto", maxLength: 140 }) }) }), _jsx(Col, { span: editMode ? 12 : 6, children: _jsx(FormItem, { label: "N\u00BA processo AIA", name: "aia_process", rules: [formRules.required], children: _jsx(Input, { placeholder: "Introduzir n\u00FAmero", maxLength: 10 }) }) }), editMode && (_jsx(Col, { span: 12, children: _jsx(FormItem, { label: "Estado", name: "status", children: _jsx(Select, { options: [{ value: 0, label: 'Em Progresso' }, { value: 1, label: 'Completo' }] }) }) })), _jsx(Col, { span: 24, children: _jsx(FormItem, { label: "Descri\u00E7\u00E3o", name: "description", children: _jsx(Input.TextArea, { showCount: { formatter: args => `${args.count}/${args.maxLength} caracteres` }, maxLength: 2000, style: { height: 225, resize: 'none' }, placeholder: "Adicionar descri\u00E7\u00E3o do projeto" }) }) }), _jsx(Col, { span: 12, children: _jsx(FormItem, { label: "Geolocaliza\u00E7\u00E3o", name: "coordinates", rules: [formRules.required], children: _jsx(MapPicker, { onModalShow: value => setShowMap(value) }) }) }), _jsx(Col, { span: 12, children: _jsx(FormItem, { label: "Membros", name: "members", rules: [formRules.required], children: _jsx(MembersInput, { members: members, onModalShow: value => setShowMembers(value), belongsToTrip: false }) }) }), _jsx(Col, { span: 12, children: _jsx(FormItem, { className: "mb-0", shouldUpdate: true, children: ({ getFieldValue }) => _jsx(FormItem, { label: "Imagem de perfil", name: "image", valuePropName: "fileList", className: "mb-0", getValueFromEvent: e => Array.isArray(e) ? e : e?.fileList, children: _jsx(Upload, { className: "project-image", listType: "picture", maxCount: 1, beforeUpload: beforeImageUpload, onChange: handleUpload, children: (!getFieldValue("image") || getFieldValue("image").length < 1) && (_jsxs("div", { className: 'd-flex align-center', children: [_jsx("p", { className: "my-0 mr-5 ant-upload-drag-icon", children: _jsx(SvgIcon, { name: "ic_picture", size: 16, color: theme["primary-100"] }) }), _jsxs("div", { style: { textAlign: 'left' }, children: [_jsx("p", { style: { color: theme["primary-100"] }, className: 'my-0 type-body-2', children: "Clique para upload" }), _jsx("p", { style: { color: theme["neutral-50"] }, className: 'my-0 type-body-1', children: "ou Arraste e largue um ficheiro" })] })] })) }) }) }) })] }) })] }));
});
